import React, { useState } from 'react';
import { isNil } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import AddIcon from '~/components/icons/AddIcon';
import PencilIcon from '~/components/icons/PencilIcon';
import InlineIconButton from '~/components/InlineIconButton';
import Switch from '~/components/ui/atoms/Switch';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import type { ReportTemplateResponse } from './hooks/useReportTemplates';
import { useReportTemplates } from './hooks/useReportTemplates';
import CreateReportTemplateDialog from './CreateReportTemplateDialog';
import EditReportTemplateDialog from './EditReportTemplateDialog';
import { getSheetCountText, getTableFallbackName, getTemplateCountText, REPORT_TEMPLATES_TEXTS } from './texts';

// Table row type that flattens the data for display
interface TemplateTableRow {
  id: number;
  name: string;
  sheets_count: number;
  sheets_summary: string;
  date_created: string;
  created_by: string;
  is_active: boolean;
  // Keep full template for actions
  _template: ReportTemplateResponse;
}

const TemplatesTab: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ReportTemplateResponse | null>(null);

  const { templates, isLoading, isError, reloadTemplates, updateTemplateStatus, isSubmitting } = useReportTemplates();

  // Transform templates to table rows
  const tableRows: TemplateTableRow[] = templates.map((template) => {
    if (isNil(template.id)) throw new Error('Template ID is required');
    const sheets = template.sheets ?? [];
    return {
      id: template.id,
      name: template.name,
      sheets_count: sheets.length,
      sheets_summary: sheets
        .map((s) => s.metabase_question_name || getTableFallbackName(s.metabase_question_id))
        .join(', '),
      date_created: template.created_at,
      created_by: template.created_by_name || REPORT_TEMPLATES_TEXTS.UNKNOWN,
      is_active: template.is_active,
      _template: template,
    };
  });

  const handleToggleActive = async (templateId: number, currentStatus: boolean) => {
    await updateTemplateStatus(templateId, !currentStatus);
  };

  const handleEditTemplate = (template: ReportTemplateResponse) => {
    setEditingTemplate(template);
  };

  const handleEditSuccess = () => {
    reloadTemplates();
  };

  const handleAddNew = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCreateSuccess = () => {
    reloadTemplates();
  };

  const columnData = [
    {
      id: 'name',
      label: REPORT_TEMPLATES_TEXTS.COLUMN_TEMPLATE_NAME,
      specialCell: (row: TemplateTableRow) => (
        <div>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
            {row.name}
          </Text>
          <Text variant={Text.VARIANTS.XS} className="text-slate-500">
            {getSheetCountText(row.sheets_count)}
          </Text>
        </div>
      ),
    },
    {
      id: 'sheets_summary',
      label: REPORT_TEMPLATES_TEXTS.COLUMN_TABLE_NAMES,
      specialCell: (row: TemplateTableRow) => (
        <div className="max-w-xs" title={row.sheets_summary}>
          <Text variant={Text.VARIANTS.SM}>{row.sheets_summary || '-'}</Text>
        </div>
      ),
    },
    {
      id: 'date_created',
      label: REPORT_TEMPLATES_TEXTS.COLUMN_DATE_CREATED,
      specialCell: (row: TemplateTableRow) => (
        <Text variant={Text.VARIANTS.SM}>{row.date_created ? serverDateTimeToLocal(row.date_created) : ''}</Text>
      ),
    },
    {
      id: 'created_by',
      label: REPORT_TEMPLATES_TEXTS.COLUMN_CREATED_BY,
      specialCell: (row: TemplateTableRow) => <Text variant={Text.VARIANTS.SM}>{row.created_by}</Text>,
    },
    {
      id: 'is_active',
      label: REPORT_TEMPLATES_TEXTS.COLUMN_ACTIVE,
      specialCell: (row: TemplateTableRow) => (
        <Switch
          checked={row.is_active}
          disabled={isSubmitting}
          onChange={() => handleToggleActive(row.id, row.is_active)}
        />
      ),
    },
    {
      id: 'actions',
      label: REPORT_TEMPLATES_TEXTS.COLUMN_ACTIONS,
      disableSort: true,
      specialCell: (row: TemplateTableRow) => (
        <InlineIconButton
          tooltipTitle={REPORT_TEMPLATES_TEXTS.EDIT}
          icon={PencilIcon}
          className="m-1 cursor-pointer text-lg"
          onClick={() => handleEditTemplate(row._template)}
        />
      ),
    },
  ];

  if (isLoading || isError) {
    return <SkeletonTable rowsCount={5} columnsCount={6} isError={isError} />;
  }

  return (
    <div className="p-16">
      <div className="mb-16 flex items-center justify-between">
        <div>
          <Heading variant={Heading.TYPES.H3}>{getTemplateCountText(templates.length)}</Heading>
        </div>
        <div>
          <Button
            color="primary"
            variant="contained"
            onClick={handleAddNew}
            startIcon={<AddIcon iconColor="currentColor" />}
          >
            {REPORT_TEMPLATES_TEXTS.ADD_NEW}
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="py-48 flex flex-col items-center justify-center">
          <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.MEDIUM} className="mb-8 text-slate-600">
            {REPORT_TEMPLATES_TEXTS.NO_TEMPLATES_YET}
          </Text>
          <Text variant={Text.VARIANTS.SM} className="mb-16 text-slate-500">
            {REPORT_TEMPLATES_TEXTS.CREATE_YOUR_FIRST_REPORT}
          </Text>
        </div>
      ) : (
        <SortableTable columns={columnData} rows={tableRows} />
      )}

      {isCreateDialogOpen && (
        <CreateReportTemplateDialog onClose={() => setIsCreateDialogOpen(false)} onSuccess={handleCreateSuccess} />
      )}

      {editingTemplate && (
        <EditReportTemplateDialog
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default TemplatesTab;
